/**
 * Balance View Component
 * Displays user's credit balance and equivalent transcription minutes
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient, type BalanceResponse } from '../services/apiClient';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ErrorMessage } from '../components/ErrorMessage';
import { formatNumber, formatDecimal } from '../utils/persian';

export const BalanceView: React.FC = () => {
  const navigate = useNavigate();
  const [balance, setBalance] = useState<BalanceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadBalance = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.getBalance();
      setBalance(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'خطا در بارگذاری اطلاعات موجودی';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBalance();
  }, [loadBalance]);

  if (loading) {
    return <LoadingSpinner message="در حال بارگذاری موجودی..." />;
  }

  if (error) {
    return <ErrorMessage message={error} onRetry={loadBalance} />;
  }

  if (!balance) {
    return null;
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h2 style={styles.title}>موجودی اعتبار</h2>
        
        <div style={styles.grid}>
          <div style={{ ...styles.statCard, ...styles.statCardBlue }}>
            <p style={styles.statLabel}>اعتبار کل</p>
            <p style={styles.statValue}>{formatNumber(balance.balance.total_credits)}</p>
            <p style={styles.statUnit}>واحد</p>
          </div>
          
          <div style={{ ...styles.statCard, ...styles.statCardGreen }}>
            <p style={styles.statLabel}>اعتبار قابل استفاده</p>
            <p style={styles.statValue}>{formatNumber(balance.balance.available_credits)}</p>
            <p style={styles.statUnit}>واحد</p>
          </div>
          
          <div style={{ ...styles.statCard, ...styles.statCardYellow }}>
            <p style={styles.statLabel}>اعتبار رزرو شده</p>
            <p style={styles.statValue}>{formatNumber(balance.balance.reserved_credits)}</p>
            <p style={styles.statUnit}>واحد</p>
          </div>
          
          <div style={{ ...styles.statCard, ...styles.statCardPurple }}>
            <p style={styles.statLabel}>معادل دقیقه</p>
            <p style={styles.statValue}>{formatDecimal(balance.balance.equivalent_minutes, 1)}</p>
            <p style={styles.statUnit}>دقیقه</p>
          </div>
        </div>

        {balance.current_plan ? (
          <div style={styles.planCard}>
            <p style={styles.planLabel}>پلن فعلی</p>
            <p style={styles.planName}>{balance.current_plan.name_fa || balance.current_plan.name}</p>
            <p style={styles.planTier}>سطح: {balance.current_plan.tier}</p>
          </div>
        ) : (
          <div style={styles.noPlanCard}>
            <p style={styles.noPlanIcon}>📦</p>
            <p style={styles.noPlanText}>
              شما هنوز پلنی خریداری نکرده‌اید.
            </p>
            <p style={styles.noPlanSubtext}>
              برای استفاده بهتر از سرویس، یک پلن انتخاب کنید.
            </p>
            <button
              style={styles.noPlanButton}
              onClick={() => navigate('/plans')}
            >
              مشاهده پلن‌ها
            </button>
          </div>
        )}
      </div>

      <div style={styles.infoCard}>
        <h3 style={styles.infoTitle}>💡 راهنما</h3>
        <ul style={styles.infoList}>
          <li style={styles.infoItem}>
            <strong>اعتبار کل:</strong> مجموع تمام اعتبارهای شما
          </li>
          <li style={styles.infoItem}>
            <strong>اعتبار قابل استفاده:</strong> اعتباری که می‌توانید برای رونویسی استفاده کنید
          </li>
          <li style={styles.infoItem}>
            <strong>اعتبار رزرو شده:</strong> اعتبار در حال استفاده برای رونویسی‌های جاری
          </li>
          <li style={styles.infoItem}>
            <strong>معادل دقیقه:</strong> تقریبی دقایق صوتی قابل رونویسی
          </li>
        </ul>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    paddingBottom: '1rem',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    padding: '1.5rem',
    marginBottom: '1rem',
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: '#1a1a1a',
    marginBottom: '1.5rem',
    textAlign: 'center',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '1rem',
    marginBottom: '1.5rem',
  },
  statCard: {
    borderRadius: '8px',
    padding: '1rem',
    textAlign: 'center',
  },
  statCardBlue: {
    backgroundColor: '#e3f2fd',
  },
  statCardGreen: {
    backgroundColor: '#e8f5e9',
  },
  statCardYellow: {
    backgroundColor: '#fff9c4',
  },
  statCardPurple: {
    backgroundColor: '#f3e5f5',
  },
  statLabel: {
    fontSize: '0.75rem',
    color: '#666',
    margin: '0 0 0.5rem 0',
  },
  statValue: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#1a1a1a',
    margin: '0.25rem 0',
  },
  statUnit: {
    fontSize: '0.75rem',
    color: '#999',
    margin: '0.25rem 0 0 0',
  },
  planCard: {
    backgroundColor: '#f5f5f5',
    borderRadius: '8px',
    padding: '1rem',
    textAlign: 'center',
  },
  planLabel: {
    fontSize: '0.75rem',
    color: '#666',
    margin: '0 0 0.5rem 0',
  },
  planName: {
    fontSize: '1.125rem',
    fontWeight: 600,
    color: '#1a1a1a',
    margin: '0.25rem 0',
  },
  planTier: {
    fontSize: '0.875rem',
    color: '#666',
    margin: '0.25rem 0 0 0',
  },
  noPlanCard: {
    backgroundColor: '#fff3e0',
    borderRadius: '8px',
    padding: '1.5rem',
    textAlign: 'center',
  },
  noPlanIcon: {
    fontSize: '2.5rem',
    margin: '0 0 0.5rem 0',
  },
  noPlanText: {
    fontSize: '0.875rem',
    color: '#e65100',
    fontWeight: 600,
    margin: '0.5rem 0',
  },
  noPlanSubtext: {
    fontSize: '0.75rem',
    color: '#666',
    margin: '0.5rem 0 1rem 0',
  },
  noPlanButton: {
    backgroundColor: '#ff9800',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '0.75rem 1.5rem',
    fontSize: '0.875rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  infoCard: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    padding: '1.5rem',
  },
  infoTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#1a1a1a',
    marginBottom: '1rem',
  },
  infoList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  infoItem: {
    fontSize: '0.875rem',
    color: '#666',
    marginBottom: '0.75rem',
    lineHeight: 1.6,
  },
};
